import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Building2, Heart, ShoppingBag, ArrowRight, Loader2, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

export default function SelectRole({ auth }) {
  const navigate = useNavigate();
  const { user, checkAuth, token } = auth;
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState('employee');
  const [companyName, setCompanyName] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API}/auth/update-role`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ 
          role: selectedRole,
          company_name: companyName 
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la mise à jour');
      }

      await checkAuth();
      toast.success('Profil mis à jour !');
      
      const dashboards = {
        company: '/company',
        association: '/association',
        ecommerce: '/ecommerce',
        employee: '/employee'
      };
      navigate(dashboards[selectedRole], { replace: true });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    {
      id: 'employee',
      label: 'Salarié',
      description: 'Gérez votre porte-monnaie et investissez',
      icon: Wallet,
      color: 'emerald'
    },
    {
      id: 'company',
      label: 'Entreprise',
      description: 'Gérez vos salariés et actions RSE',
      icon: Building2,
      color: 'slate'
    },
    {
      id: 'association',
      label: 'Association',
      description: 'Créez des campagnes de collecte',
      icon: Heart,
      color: 'orange'
    },
    {
      id: 'ecommerce',
      label: 'E-commerce',
      description: 'Proposez vos promotions',
      icon: ShoppingBag,
      color: 'slate'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-emerald-50 to-orange-50 flex items-center justify-center px-6 py-12">
      <div className="absolute inset-0 grain"></div>
      
      <div className="w-full max-w-lg relative z-10">
        <Card className="bg-white/80 backdrop-blur-xl border-stone-200 rounded-2xl shadow-xl animate-slide-up">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Bienvenue, {user?.name} !
            </CardTitle>
            <CardDescription className="text-slate-600">
              Sélectionnez votre type de compte
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-4">
                {roles.map((role) => (
                  <button
                    key={role.id}
                    type="button"
                    onClick={() => setSelectedRole(role.id)}
                    className={`p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                      selectedRole === role.id
                        ? role.color === 'emerald'
                          ? 'border-emerald-500 bg-emerald-50'
                          : role.color === 'orange'
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-slate-500 bg-slate-50'
                        : 'border-stone-200 hover:border-stone-300 bg-white'
                    }`}
                    data-testid={`role-${role.id}-btn`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        role.color === 'emerald'
                          ? 'bg-emerald-800'
                          : role.color === 'orange'
                          ? 'bg-orange-600'
                          : 'bg-slate-800'
                      }`}>
                        <role.icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{role.label}</p>
                        <p className="text-sm text-slate-500">{role.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_name" className="text-slate-700">
                  Nom de {selectedRole === 'association' ? 'l\'association' : selectedRole === 'ecommerce' ? 'l\'entreprise' : 'l\'entreprise'}
                </Label>
                <Input
                  id="company_name"
                  type="text"
                  placeholder={selectedRole === 'association' ? 'Mon Association' : 'Ma Société SAS'}
                  className="py-6 bg-stone-50 border-stone-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  data-testid="company-name-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full py-6 bg-emerald-800 hover:bg-emerald-900 text-white rounded-xl font-semibold"
                disabled={loading || !companyName}
                data-testid="confirm-role-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Continuer
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
